/**
 * Search Input Typewriter Effect
 * Adds dynamic placeholder with rotating collection names
 * 
 * Usage:
 * - Automatically initializes on page load
 * - Works with all .search__input and input[type="search"] elements
 * - Pauses when input is focused or has value
 * 
 * Customize:
 * - window.searchTypewriter.setCollections(['Item 1', 'Item 2', ...])
 * - window.searchTypewriter.addCollections(['New Item'])
 */

class SearchTypewriter {
  constructor() {
    // Collection names to rotate through (top 10 from the list)
    this.collections = [
      'Side Stand',
      'Footrest',
      'Visor',
      'Airways',
      'Tank Fairing',
      'Top Box Mount',
      'Handlebar Riser',
      'GPS Mount',
      'Mirror',
      'Chain Cover'
    ];
    
    this.staticText = "I'm looking for ";
    this.typingSpeed = 100; // milliseconds per character
    this.deletingSpeed = 50; // milliseconds per character
    this.delayBetweenWords = 2000; // pause after complete word
    this.delayBeforeDelete = 1500; // pause before deleting
    
    this.currentCollectionIndex = 0;
    this.currentCharIndex = 0;
    this.isDeleting = false;
    this.searchInputs = [];
    this.showCursor = true;
    this.cursorBlinkInterval = null;
    
    this.init();
    this.startCursorBlink();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.findInputs());
    } else {
      this.findInputs();
    }
    
    // Re-check for inputs when drawer/modal opens
    document.addEventListener('click', () => {
      setTimeout(() => this.findInputs(), 100);
    });
  }

  findInputs() {
    // Find all search inputs
    const inputs = document.querySelectorAll('.desk-search-input, input[type="search"]');
    
    inputs.forEach(input => {
      // Skip if already initialized
      if (input.dataset.typewriterInit) return;
      
      // Mark as initialized
      input.dataset.typewriterInit = 'true';
      input.classList.add('has-typewriter-label');
      
      // Store original placeholder
      if (!input.dataset.originalPlaceholder) {
        input.dataset.originalPlaceholder = input.placeholder || '';
      }
      
      // Clear native placeholder
      input.placeholder = '';
      
      // Ensure parent has relative positioning for absolute label
      const parent = input.parentElement;
      if (parent) {
        const computedStyle = window.getComputedStyle(parent);
        if (computedStyle.position === 'static') {
          parent.style.position = 'relative';
        }
      }
      
      // Create custom label element with spans for styling
      const label = document.createElement('label');
      label.className = 'typewriter-label';
      label.innerHTML = `
        <span class="static-text">${this.staticText}</span><span class="dynamic-text"></span><span class="cursor">|</span>
      `;
      
      // Insert label after input
      input.parentElement.insertBefore(label, input.nextSibling);
      
      // Track input value changes
      input.addEventListener('input', () => {
        if (input.value.trim() !== '') {
          input.classList.add('has-value');
        } else {
          input.classList.remove('has-value');
        }
      });
      
      this.searchInputs.push(input);
    });
    
    // Start animation if we have inputs and not already running
    if (this.searchInputs.length > 0 && !this.animationRunning) {
      this.animationRunning = true;
      this.animate();
    }
  }

  startCursorBlink() {
    // Blink cursor every 530ms
    this.cursorBlinkInterval = setInterval(() => {
      this.showCursor = !this.showCursor;
      // Only update cursor, not full animation
      this.searchInputs.forEach(input => {
        const label = input.parentElement.querySelector('.typewriter-label');
        if (label) {
          const cursor = label.querySelector('.cursor');
          if (cursor) {
            cursor.style.opacity = this.showCursor ? '1' : '0';
          }
        }
      });
    }, 530);
  }

  updatePlaceholders() {
    const currentCollection = this.collections[this.currentCollectionIndex];
    
    // Update all search inputs
    this.searchInputs.forEach(input => {
      // Find the label for this input
      const label = input.parentElement.querySelector('.typewriter-label');
      if (!label) return;
      
      const dynamicText = label.querySelector('.dynamic-text');
      const cursor = label.querySelector('.cursor');
      
      if (!dynamicText || !cursor) return;
      
      // Get the current portion of collection name
      let collectionPortion = '';
      if (this.isDeleting) {
        // Deleting animation
        collectionPortion = currentCollection.substring(0, this.currentCharIndex);
      } else {
        // Typing animation
        collectionPortion = currentCollection.substring(0, this.currentCharIndex);
      }
      
      // Update dynamic text span
      dynamicText.textContent = collectionPortion;
    });
  }

  animate() {
    const currentCollection = this.collections[this.currentCollectionIndex];
    
    // Update placeholders
    this.updatePlaceholders();
    
    // Calculate next step
    let nextDelay = this.isDeleting ? this.deletingSpeed : this.typingSpeed;
    
    if (!this.isDeleting && this.currentCharIndex === currentCollection.length) {
      // Finished typing, wait before deleting
      nextDelay = this.delayBeforeDelete;
      this.isDeleting = true;
    } else if (this.isDeleting && this.currentCharIndex === 0) {
      // Finished deleting, move to next collection
      this.isDeleting = false;
      this.currentCollectionIndex = (this.currentCollectionIndex + 1) % this.collections.length;
      nextDelay = this.delayBetweenWords / 2;
    }
    
    // Update character index
    if (this.isDeleting) {
      this.currentCharIndex--;
    } else {
      this.currentCharIndex++;
    }
    
    // Continue animation
    setTimeout(() => this.animate(), nextDelay);
  }

  // Public method to add custom collections
  addCollections(newCollections) {
    if (Array.isArray(newCollections)) {
      this.collections = [...this.collections, ...newCollections];
    }
  }

  // Public method to set collections
  setCollections(collections) {
    if (Array.isArray(collections) && collections.length > 0) {
      this.collections = collections;
      this.currentCollectionIndex = 0;
    }
  }
}

// Initialize typewriter effect
if (typeof window !== 'undefined') {
  window.searchTypewriter = new SearchTypewriter();
  
  // Export for external use
  window.SearchTypewriter = SearchTypewriter;
}

